import {
  type ComboboxPositionerProps,
  type ComboboxRootProps,
  Combobox,
} from '@base-ui/react/combobox';
import { merge } from 'lodash-es';
import * as React from 'react';

import type { VariantProp } from '../../common/colors';
import type {
  ComboBoxItemsProp,
  ComboBoxRow,
  ComboBoxRowGroup,
} from '../../components/shared/ComboBox';
import type { DisplayProps } from '../../utilities/shared/Display';
import type {
  ControlMaxWidth,
  ControlMinWidth,
  ControlSize,
  ControlVariantInput,
  ControlWidth,
} from '../shared/types';
import { type IconName, Icon } from '../../assets/Icon/Icon';
import { shadows } from '../../common/shadows';
import {
  getVariantStyles,
  variantAccentColor,
  variantBackgroundColor,
  variantBodyColor,
  variantHeadingColor,
  variantIconColor,
  variantStrokeColor,
} from '../../common/variant';
import { stateZIndexes } from '../../common/z_indexes';
import {
  ComboBoxEmpty,
  ComboBoxGroup,
  ComboBoxGroupLabel,
  comboBoxIsItemEqualToValue,
  ComboBoxList,
  ComboBoxOption,
  ComboBoxPopup,
  ComboBoxPortal,
  ComboBoxPositioner,
  comboBoxRowLabel,
  ComboBoxSearch,
  useCollectionFromItems,
  useComboBoxFilter,
  useComboBoxLabel,
} from '../../components/shared/ComboBox';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { LargeSansSizes } from '../../text/Large';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { displayCSS } from '../../utilities/shared/Display';
import { type MaxWidthTypes, maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { type MinWidthTypes, minWidthCSS } from '../../utilities/shared/MinWidth';
import { type WidthTypes, widthCSS } from '../../utilities/shared/Width';
import { useControllableState } from '../../utilities/useControllableState';
import {
  ControlProvider,
  useControlMaxWidth,
  useControlMinWidth,
  useControlSize,
  useControlVariant,
  useControlWidth,
} from '../shared/context';
import { selectors, transitions } from '../shared/styles';
import {
  controlArrowsSize,
  controlBorderRadius,
  controlGap,
  controlIconSize,
  controlPaddingX,
  controlPaddingY,
  controlTextDecoration,
  controlTextDecorationValue,
  controlVars,
  getControlSubcontrolSize,
} from '../shared/vars';

const InlineSelectArrows = styled(Icon, {
  position: 'absolute',
  top: '50%',
  right: controlPaddingX,
  zIndex: stateZIndexes.fixture,
  size: controlArrowsSize,
  color: variantIconColor,
  pointerEvents: 'none',
  opacity: '$$arrowsOpacity',
  transform: 'translateY(-50%)',
});
InlineSelectArrows.displayName = 'InlineSelectArrows';

const InlineSelectIcon = styled(Icon, {
  size: controlIconSize,
  color: variantIconColor,
});
InlineSelectIcon.displayName = 'InlineSelectIcon';

// `Combobox.Value` renders no element of its own, and the label is resolved here anyway, so this is
// a plain span carrying the `data-placeholder` the trigger used to get from Base UI.
const InlineSelectValue = styled('span', {
  width: '100%',
  color: variantBodyColor,
  fontWeight: '$bold',
  textAlign: 'left',
  truncate: true,
  transition: transitions.control,
  textDecoration: controlTextDecoration,

  '&[data-placeholder]': {
    color: variantAccentColor,
  },

  variants: {
    size: {
      'x-small': { ...SmallSansSizes },
      small: { ...SmallSansSizes },
      medium: { ...BodySansSizes },
      large: { ...BodySansSizes },
      'x-large': { ...LargeSansSizes },
    },
  },
});
InlineSelectValue.displayName = 'InlineSelectValue';

const InlineSelectTrigger = styled(Combobox.Trigger, {
  zIndex: stateZIndexes.initial,
  display: 'inline-flex',
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
  padding: 0,
  margin: 0,
  background: colors.transparent,
  border: 'none',
  color: variantBodyColor,
  font: 'inherit',
  lineHeight: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',

  [selectors.focus]: {
    outline: 'none',
  },

  '&[data-disabled]': {
    cursor: 'not-allowed',
  },
});
InlineSelectTrigger.displayName = 'InlineSelectTrigger';

const InlineSelectElements = styled('div', {
  position: 'relative',
  zIndex: stateZIndexes.initial,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: controlGap,
  width: '100%',
  minWidth: 0,
});
InlineSelectElements.displayName = 'InlineSelectElements';

const InlineSelectBoundary = styled('div', {
  position: 'absolute',
  zIndex: stateZIndexes.boundary,
  top: `calc(${controlPaddingY} * -1)`,
  bottom: `calc(${controlPaddingY} * -1)`,
  left: `calc(${controlPaddingX} * -1)`,
  background: '$$boundaryBackground',
  strokeAll: variantStrokeColor,
  borderRadius: controlBorderRadius,
  opacity: '$$boundaryOpacity',
  transition: transitions.control,
  pointerEvents: 'none',

  variants: {
    inlineChevrons: {
      true: {
        right: `calc((${controlPaddingX}) * -1)`,
      },
      false: {
        right: `calc((${controlPaddingX} + ${controlArrowsSize} + ${controlGap}) * -1)`,
      },
    },
  },
});
InlineSelectBoundary.displayName = 'InlineSelectBoundary';

const InlineSelectContainer = styled('div', {
  position: 'relative',
  zIndex: stateZIndexes.initial,
  display: 'flex',
  minWidth: 0,
  maxWidth: '100%',
  transition: transitions.control,
  cursor: 'pointer',
  $$boundaryBackground: colors.bgApplicationLight,
  $$boundaryOpacity: 0,
  $$arrowsOpacity: 0,
  [controlTextDecoration]: controlTextDecorationValue,

  [darkThemeSelector]: {
    $$boundaryBackground: colors.bgApplicationDark,
  },

  [selectors.hover]: {
    zIndex: stateZIndexes.hover,
  },

  [selectors.focus]: {
    zIndex: stateZIndexes.focus,
    outline: 'none',

    [`& ${InlineSelectBoundary}`]: {
      boxShadow: shadows.focusRingLight,

      [darkThemeSelector]: {
        boxShadow: shadows.focusRingDark,
      },
    },
  },

  [selectors.interactive]: {
    $$boundaryOpacity: 1,
    $$arrowsOpacity: 1,
    [controlTextDecoration]: 'none',
  },

  '&:has([data-popup-open])': {
    zIndex: stateZIndexes.focus,
    $$boundaryOpacity: 1,
    $$arrowsOpacity: 1,
    [controlTextDecoration]: 'none',
  },

  variants: {
    disabled: {
      true: {
        opacity: 0.5,
        pointerEvents: 'none',
      },
      false: {},
    },
    size: {
      'x-small': {
        ...controlVars.size['x-small'],
        ...SmallSansSizes,
      },
      small: {
        ...controlVars.size.small,
        ...SmallSansSizes,
      },
      medium: {
        ...controlVars.size.medium,
        ...BodySansSizes,
      },
      large: {
        ...controlVars.size.large,
        ...BodySansSizes,
      },
      'x-large': {
        ...controlVars.size['x-large'],
        ...LargeSansSizes,
      },
    },
    variant: {
      alternative: { ...getVariantStyles('alternative') },
      attention: { ...getVariantStyles('attention') },
      brand: { ...getVariantStyles('brand') },
      neutral: { ...getVariantStyles('neutral') },
      negative: { ...getVariantStyles('negative') },
      positive: { ...getVariantStyles('positive') },
    },
    invalid: {
      true: {
        ...getVariantStyles('negative'),
      },
      false: {},
    },
  },
});
InlineSelectContainer.displayName = 'InlineSelectContainer';

const InlineSelectItemDescription = styled(Text, {
  ...SmallSansSizes,
  color: '$$itemTextColor',

  variants: {
    size: {
      'x-small': { ...SmallSansSizes },
      small: { ...SmallSansSizes },
      medium: { ...BodySansSizes },
      large: { ...BodySansSizes },
      'x-large': { ...LargeSansSizes },
    },
    variant: {
      alternative: { ...getVariantStyles('alternative') },
      attention: { ...getVariantStyles('attention') },
      brand: { ...getVariantStyles('brand') },
      neutral: { ...getVariantStyles('neutral') },
      negative: { ...getVariantStyles('negative') },
      positive: { ...getVariantStyles('positive') },
    },
  },
});
InlineSelectItemDescription.displayName = 'InlineSelectItemDescription';

const InlineSelectItemText = styled('span', {
  display: 'flex',
  alignItems: 'stretch',
  color: '$$itemTextColor',
  fontWeight: '$bold',

  variants: {
    size: {
      'x-small': { ...SmallSansSizes },
      small: { ...SmallSansSizes },
      medium: { ...BodySansSizes },
      large: { ...BodySansSizes },
      'x-large': { ...LargeSansSizes },
    },
    variant: {
      alternative: { ...getVariantStyles('alternative') },
      attention: { ...getVariantStyles('attention') },
      brand: { ...getVariantStyles('brand') },
      neutral: { ...getVariantStyles('neutral') },
      negative: { ...getVariantStyles('negative') },
      positive: { ...getVariantStyles('positive') },
    },
  },
});
InlineSelectItemText.displayName = 'InlineSelectItemText';

const InlineSelectItemRoot = styled(ComboBoxOption, {
  flexDirection: 'column',
  $$itemTextColor: variantHeadingColor,

  '&[data-highlighted]': {
    backgroundColor: variantBackgroundColor,
  },

  '&[data-selected]': {
    backgroundColor: colors.bgBrandLight,
    $$itemTextColor: colors.headingBrandLight,

    [darkThemeSelector]: {
      backgroundColor: colors.bgBrandDark,
      $$itemTextColor: colors.headingBrandDark,
    },
  },

  variants: {
    hasDescription: {
      true: { alignItems: 'flex-start' },
      false: {},
    },
    size: {
      'x-small': { ...SmallSansSizes },
      small: { ...SmallSansSizes },
      medium: { ...BodySansSizes },
      large: { ...BodySansSizes },
      'x-large': { ...LargeSansSizes },
    },
    variant: {
      alternative: { ...getVariantStyles('alternative') },
      attention: { ...getVariantStyles('attention') },
      brand: { ...getVariantStyles('brand') },
      neutral: { ...getVariantStyles('neutral') },
      negative: { ...getVariantStyles('negative') },
      positive: { ...getVariantStyles('positive') },
    },
  },
});
InlineSelectItemRoot.displayName = 'InlineSelectItemRoot';

interface InlineSelectItemProps {
  row: ComboBoxRow;
  size?: ControlSize;
  variant?: VariantProp;
}

function InlineSelectItemImpl({ row, size, variant = 'neutral' }: InlineSelectItemProps) {
  const effectiveSize = useControlSize(size, 'medium');

  return (
    <InlineSelectItemRoot
      value={row.value}
      disabled={row.disabled}
      size={effectiveSize}
      variant={variant}
      hasDescription={!!row.description}
    >
      <InlineSelectItemText size={effectiveSize} variant={variant}>
        {row.label}
      </InlineSelectItemText>
      {row.description && (
        <InlineSelectItemDescription variant={variant}>
          {row.description}
        </InlineSelectItemDescription>
      )}
    </InlineSelectItemRoot>
  );
}

/**
 * A single option. Memoized because the list re-renders on every highlight change, and each Base UI
 * item underneath runs a dozen hooks and four store subscriptions — bailing out on the rows that
 * didn't change is what keeps a long list typable. The bailout only holds while the props stay
 * referentially stable, hence the memoized collection and `ControlProvider` values below.
 */
const InlineSelectItem = React.memo(InlineSelectItemImpl);
InlineSelectItem.displayName = 'InlineSelectItem';

type InlineSelectInheritedRootProps = Omit<
  ComboboxRootProps<string, false>,
  | 'children'
  | 'defaultInputValue'
  | 'filter'
  | 'inputValue'
  | 'isItemEqualToValue'
  | 'items'
  | 'itemToStringLabel'
  | 'onValueChange'
>;

export interface InlineSelectProps extends DisplayProps, InlineSelectInheritedRootProps {
  /**
   * Items for the trigger's label and for the dropdown list.
   *
   * **`Record`** for static maps, **`{ value, label, description? }[]`** for ordered options, or **`{ label?, items }[]`**
   * for grouped sections. `description` appears as a secondary line inside the dropdown only — it is
   * never mirrored into the closed trigger.
   */
  items: ComboBoxItemsProp;
  /**
   * Called when the selected value changes (`eventDetails` from Base UI is not surfaced here).
   */
  onValueChange?: (value: string | null) => void;
  /**
   * Accessible label for the trigger when no visible label exists.
   */
  'aria-label'?: string;
  /**
   * ID of an element that labels the trigger.
   */
  'aria-labelledby'?: string;
  /**
   * Optional leading icon, rendered next to the selected value.
   */
  icon?: IconName;
  /**
   * Whether to display the trigger in an indeterminate state (e.g. for a multi-row form
   * where selections disagree). Shows an em-dash by default, or the placeholder when
   * `showPlaceholderForIndeterminate` is set.
   */
  indeterminate?: boolean;
  /**
   * Whether to display the chevrons inside the trigger or inside the boundary.
   */
  inlineChevrons?: boolean;
  /**
   * Whether the select is invalid. Applies the `negative` variant palette.
   */
  invalid?: boolean;
  /**
   * Overrides the trigger's label for the selected value. Options in the list are unaffected.
   */
  itemToStringLabel?: (value: string) => React.ReactNode;
  /**
   * Props forwarded to the underlying `Combobox.Positioner`. Use for `sideOffset`, `align`,
   * `collisionAvoidance`, etc.
   *
   * The popup anchors to the hover/focus boundary rather than the trigger, so `align` lines up
   * with the visible chip edge instead of the bare text. Pass `anchor` to opt out.
   */
  positionerProps?: Omit<ComboboxPositionerProps, 'children' | 'className' | 'style'> & {
    width?: {
      width?: WidthTypes;
      maxWidth?: MaxWidthTypes;
      minWidth?: MinWidthTypes;
    };
  };
  /**
   * Copy for the empty selection so the inline dotted underline is visible; without it the
   * trigger can look like plain body text.
   */
  placeholder: string;
  /**
   * When combined with `indeterminate`, show the placeholder string instead of the
   * default em-dash.
   */
  showPlaceholderForIndeterminate?: boolean;
  /**
   * When true, renders a search field at the top of the dropdown for filtering items.
   */
  searchable?: boolean;
  /**
   * Placeholder text for the search field when `searchable` is true. Defaults to "Search…".
   */
  searchPlaceholder?: string;
  /**
   * The size of the inline trigger (controls text and spacing).
   */
  size?: ControlSize;
  /**
   * Optional text (or node) rendered after the value, inline with the trigger.
   */
  suffix?: React.ReactNode;
  /**
   * Colour variant for value/icon/stroke using the design system semantic palettes.
   */
  variant?: ControlVariantInput;
  /**
   * Constrains the container width. Accepts any CSS width string or number.
   */
  width?: ControlWidth;
  /**
   * Constrains the container maxWidth.
   */
  maxWidth?: ControlMaxWidth;
  /**
   * Constrains the container minWidth.
   */
  minWidth?: ControlMinWidth;
}

/** Hoisted so the common case doesn't hand the positioner a fresh object on every render. */
const DEFAULT_POSITIONER_PROPS: NonNullable<InlineSelectProps['positionerProps']> = {
  width: { maxWidth: 320 },
};

export const InlineSelect = React.forwardRef<HTMLButtonElement, InlineSelectProps>((props, ref) => {
  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    defaultValue,
    disabled,
    display,
    icon,
    id,
    items: itemsProp,
    indeterminate,
    invalid,
    inlineChevrons = false,
    itemToStringLabel,
    maxWidth,
    minWidth,
    placeholder,
    positionerProps = DEFAULT_POSITIONER_PROPS,
    searchable = false,
    searchPlaceholder = 'Search…',
    showPlaceholderForIndeterminate,
    size,
    suffix,
    value,
    variant,
    width,
    onValueChange,
    ...rootProps
  } = props;

  const controlSize = useControlSize(size, 'medium');
  const effectiveSubcontrolSize = getControlSubcontrolSize(controlSize);
  const controlVariant = useControlVariant(variant, 'neutral');
  const controlWidth = useControlWidth(width);
  const controlMaxWidth = useControlMaxWidth(maxWidth);
  const controlMinWidth = useControlMinWidth(minWidth);

  // The trigger is bare text — the visible chrome is `InlineSelectBoundary`, which overhangs it
  // by the control's padding on every side (plus the arrows when they sit outside the trigger).
  // Anchoring the popup to the boundary makes `align` and `sideOffset` measure from the edges the
  // user can actually see, and `--anchor-width` match the chip rather than the text.
  const boundaryRef = React.useRef<HTMLDivElement>(null);
  const {
    align: positionerAlign = 'start',
    anchor = boundaryRef,
    collisionPadding = 8,
    sideOffset = 4,
    // Sizing is ours, not Base UI's, so it has to come out of the props spread.
    width: popoverWidth,
    ...restPositionerProps
  } = positionerProps;

  // A fresh object here would make Stitches recompute and re-inject the popup's class on every
  // keystroke, so it's keyed on the three primitives that actually decide it.
  const popoverCSS = React.useMemo(
    () =>
      merge(
        widthCSS(popoverWidth?.width),
        maxWidthCSS(popoverWidth?.maxWidth),
        minWidthCSS(popoverWidth?.minWidth),
      ),
    [popoverWidth?.width, popoverWidth?.maxWidth, popoverWidth?.minWidth],
  );

  // Folding and flattening `items` is the expensive half of a search and doesn't depend on the
  // query, so it happens once per `items` rather than once per keystroke.
  const collection = useCollectionFromItems(itemsProp);
  const matchesQuery = useComboBoxFilter();

  const [resolvedValue, setResolvedValue] = useControllableState<string | null>({
    defaultValue: defaultValue ?? null,
    onChange: onValueChange,
    value,
  });

  const handleValueChange = React.useCallback(
    (next: string | null) => setResolvedValue(next),
    [setResolvedValue],
  );

  const showChevron = !disabled;

  let selectedLabel: React.ReactNode;
  if (indeterminate) {
    selectedLabel = showPlaceholderForIndeterminate ? placeholder : <>&ndash;&nbsp;</>;
  } else if (resolvedValue == null) {
    selectedLabel = placeholder;
  } else if (itemToStringLabel) {
    selectedLabel = itemToStringLabel(resolvedValue);
  } else {
    selectedLabel = collection.byValue.get(resolvedValue)?.label ?? resolvedValue;
  }

  const containerCSS = displayCSS(display);

  // Memoized so a keystroke doesn't invalidate the context every item reads for its size — a new
  // value object there re-renders all of them and undoes `InlineSelectItem`'s memoization.
  const subcontrolControl = React.useMemo(
    () => ({ size: effectiveSubcontrolSize }),
    [effectiveSubcontrolSize],
  );
  const listControl = React.useMemo(() => ({ size: controlSize }), [controlSize]);

  const renderRow = React.useCallback(
    (row: ComboBoxRow) => <InlineSelectItem key={row.value} row={row} />,
    [],
  );

  const renderGroup = React.useCallback(
    (group: ComboBoxRowGroup) => (
      <ComboBoxGroup key={group.key} items={group.items}>
        {group.label != null && <ComboBoxGroupLabel>{group.label}</ComboBoxGroupLabel>}
        <Combobox.Collection>{renderRow}</Combobox.Collection>
      </ComboBoxGroup>
    ),
    [renderRow],
  );

  // An explicit `Combobox.Collection` rather than a function child of the list: 1.3.0 caches the
  // first function child it sees in a module-level variable, so every combobox in the app would
  // render this one's rows. Memoizing the element also lets React bail out of the subtree when the
  // list re-renders for a highlight change — refiltering still lands, because it arrives through
  // context rather than through props.
  const listChildren = React.useMemo(
    () => (
      <ControlProvider value={listControl}>
        <Combobox.Collection>{collection.groups ? renderGroup : renderRow}</Combobox.Collection>
      </ControlProvider>
    ),
    [collection.groups, listControl, renderGroup, renderRow],
  );

  const chevrons = (
    <ControlProvider value={subcontrolControl}>
      <InlineSelectArrows icon="chevrons-vertical" />
    </ControlProvider>
  );

  const { labelProps } = useComboBoxLabel({
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    placeholder,
  });

  const [query, setQuery] = React.useState('');
  const { onInputValueChange, onOpenChange } = rootProps;

  // Base UI passes `filter` the query it seeds from the selected value's label rather than the one
  // being typed, so a reopened list would never reach past the row already chosen.
  const filter = React.useCallback(
    (row: unknown) => matchesQuery(row, query),
    [matchesQuery, query],
  );

  const handleInputValueChange = React.useCallback(
    (next: string, details: Combobox.Root.ChangeEventDetails) => {
      setQuery(next);
      onInputValueChange?.(next, details);
    },
    [onInputValueChange],
  );

  // Base UI clears the query only once a close has finished, so reopening mid-exit keeps the last
  // search. Clearing on open makes it unconditional.
  const handleOpenChange = React.useCallback(
    (next: boolean, details: Combobox.Root.ChangeEventDetails) => {
      if (next) setQuery('');
      onOpenChange?.(next, details);
    },
    [onOpenChange],
  );

  return (
    <Combobox.Root<string, false>
      {...rootProps}
      id={id}
      disabled={disabled}
      items={collection.groups ?? collection.rows}
      filter={filter}
      itemToStringLabel={comboBoxRowLabel}
      isItemEqualToValue={comboBoxIsItemEqualToValue}
      inputValue={query}
      onInputValueChange={handleInputValueChange}
      onOpenChange={handleOpenChange}
      value={resolvedValue}
      onValueChange={handleValueChange}
    >
      <InlineSelectContainer
        size={controlSize}
        variant={controlVariant}
        disabled={disabled ?? false}
        invalid={invalid ?? false}
        style={{
          width: controlWidth,
          maxWidth: controlMaxWidth,
          minWidth: controlMinWidth,
        }}
        css={containerCSS}
      >
        <InlineSelectElements>
          <ControlProvider value={subcontrolControl}>
            {icon && <InlineSelectIcon icon={icon} />}
          </ControlProvider>
          <InlineSelectTrigger
            ref={ref}
            id={id}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
          >
            <InlineSelectValue
              size={controlSize}
              data-placeholder={resolvedValue == null ? '' : undefined}
            >
              {selectedLabel}
            </InlineSelectValue>
            {suffix}
          </InlineSelectTrigger>
        </InlineSelectElements>
        {showChevron && inlineChevrons ? chevrons : null}
        <InlineSelectBoundary ref={boundaryRef} inlineChevrons={inlineChevrons}>
          {showChevron && !inlineChevrons ? chevrons : null}
        </InlineSelectBoundary>
      </InlineSelectContainer>
      <ComboBoxPortal>
        <ComboBoxPositioner
          {...restPositionerProps}
          align={positionerAlign}
          anchor={anchor}
          collisionPadding={collisionPadding}
          sideOffset={sideOffset}
          css={popoverCSS}
        >
          <ComboBoxPopup {...labelProps} css={popoverCSS}>
            {searchable && <ComboBoxSearch placeholder={searchPlaceholder} />}
            {/* With no search field there is no input to take focus, so the list itself becomes the
                keyboard target Base UI drives through `aria-activedescendant`. */}
            <ComboBoxList {...labelProps} tabIndex={searchable ? undefined : 0}>
              {listChildren}
            </ComboBoxList>
            <ComboBoxEmpty>No results</ComboBoxEmpty>
          </ComboBoxPopup>
        </ComboBoxPositioner>
      </ComboBoxPortal>
    </Combobox.Root>
  );
});
InlineSelect.displayName = 'InlineSelect';
